/**
 * InputView Component
 *
 * Initial view where users input:
 * - Polymarket URL
 * - YouTube URL
 * - Start time (EST)
 * - End time (EST)
 */

import React, { useState } from 'react';
import { COLORS, BORDER_RADIUS, SPACING, FONT_SIZES } from '../data/constants';
import polymarketLogo from '../images/polymarket_logo.png';

const InputView = ({ onSubmit }) => {
  const [polymarketUrl, setPolymarketUrl] = useState('https://polymarket.com/event/will-the-supreme-court-rule-in-favor-of-trumps-tariffs');
  const [youtubeUrl, setYoutubeUrl] = useState('https://www.youtube.com/watch?v=LXhzp0omPe0');
  const [startTime, setStartTime] = useState('2025-11-05T08:07');
  const [endTime, setEndTime] = useState('2025-11-05T12:37');
  const [errors, setErrors] = useState({});

  const validateInputs = () => {
    const newErrors = {};

    if (!polymarketUrl.trim()) {
      newErrors.polymarketUrl = 'Polymarket URL is required';
    } else if (!polymarketUrl.includes('polymarket.com')) {
      newErrors.polymarketUrl = 'Please enter a valid Polymarket URL';
    }

    if (!youtubeUrl.trim()) {
      newErrors.youtubeUrl = 'YouTube URL is required';
    } else if (!youtubeUrl.includes('youtube.com') && !youtubeUrl.includes('youtu.be')) {
      newErrors.youtubeUrl = 'Please enter a valid YouTube URL';
    }

    if (!startTime.trim()) {
      newErrors.startTime = 'Start time is required';
    }

    if (!endTime.trim()) {
      newErrors.endTime = 'End time is required';
    }

    if (startTime && endTime && new Date(startTime) >= new Date(endTime)) {
      newErrors.endTime = 'End time must be after start time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateInputs()) {
      onSubmit({
        polymarketUrl,
        youtubeUrl,
        startTime,
        endTime
      });
    }
  };

  return (
    <div style={styles.container}>
      {/* Decorative background elements */}
      <div style={styles.bgGlow} />
      <div style={styles.bgGlow2} />

      <div className="input-card" style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logoBadge}>
            <img src={polymarketLogo} alt="Polymarket" style={styles.logo} />
          </div>
          <h1 style={styles.title}>PolyVideo</h1>
          <p style={styles.subtitle}>
            Synchronize prediction market odds with live video to pinpoint the moments that moved markets.
          </p>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Polymarket URL */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <span style={styles.labelIcon}>&#x1F4CA;</span>
              Polymarket Market URL
            </label>
            <input
              type="text"
              value={polymarketUrl}
              onChange={(e) => setPolymarketUrl(e.target.value)}
              placeholder="https://polymarket.com/event/..."
              style={{
                ...styles.input,
                ...(errors.polymarketUrl ? styles.inputError : {})
              }}
            />
            {errors.polymarketUrl && (
              <span style={styles.errorText}>{errors.polymarketUrl}</span>
            )}
          </div>

          {/* YouTube URL */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <span style={styles.labelIcon}>&#x25B6;</span>
              YouTube Video URL
            </label>
            <input
              type="text"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              style={{
                ...styles.input,
                ...(errors.youtubeUrl ? styles.inputError : {})
              }}
            />
            {errors.youtubeUrl && (
              <span style={styles.errorText}>{errors.youtubeUrl}</span>
            )}
          </div>

          {/* Time range */}
          <div style={styles.timeSection}>
            <label style={styles.timeSectionLabel}>
              <span style={styles.labelIcon}>&#x1F552;</span>
              Time Range (EST)
            </label>
            <div className="input-time-row" style={styles.row}>
              <div style={styles.timeGroup}>
                <span style={styles.timeLabel}>From</span>
                <input
                  type="datetime-local"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  style={{
                    ...styles.input,
                    ...styles.timeInput,
                    ...(errors.startTime ? styles.inputError : {})
                  }}
                />
                {errors.startTime && (
                  <span style={styles.errorText}>{errors.startTime}</span>
                )}
              </div>

              <div className="input-time-dash" style={styles.timeDash}>&#x2192;</div>

              <div style={styles.timeGroup}>
                <span style={styles.timeLabel}>To</span>
                <input
                  type="datetime-local"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  style={{
                    ...styles.input,
                    ...styles.timeInput,
                    ...(errors.endTime ? styles.inputError : {})
                  }}
                />
                {errors.endTime && (
                  <span style={styles.errorText}>{errors.endTime}</span>
                )}
              </div>
            </div>
          </div>

          {/* Submit */}
          <button type="submit" style={styles.button}>
            Analyze Market & Video
            <span style={styles.buttonArrow}>&#x2192;</span>
          </button>
        </form>

        {/* Footer hint */}
        <p style={styles.footerText}>
          All times are interpreted as EST/EDT timezone.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: COLORS.PRIMARY_DARK,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.LG,
    position: 'relative',
    overflow: 'hidden',
  },
  bgGlow: {
    position: 'absolute',
    top: '-20%',
    right: '-10%',
    width: '600px',
    height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(43, 151, 212, 0.12) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  bgGlow2: {
    position: 'absolute',
    bottom: '-15%',
    left: '-10%',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(0, 211, 149, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    backgroundColor: COLORS.WHITE,
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255,255,255,0.05)',
    maxWidth: '560px',
    width: '100%',
    padding: '40px',
    position: 'relative',
    zIndex: 1,
  },
  header: {
    textAlign: 'center',
    marginBottom: SPACING.LG,
  },
  logoBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f4f8',
    borderRadius: '14px',
    padding: '12px 16px',
    marginBottom: SPACING.MD,
  },
  logo: {
    height: '36px',
    width: 'auto',
    display: 'block',
  },
  title: {
    fontSize: '36px',
    fontWeight: '800',
    color: COLORS.PRIMARY_DARK,
    margin: `${SPACING.XS} 0`,
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: FONT_SIZES.SM,
    color: '#6b7c8d',
    lineHeight: '1.6',
    maxWidth: '380px',
    margin: '0 auto',
    marginTop: SPACING.XS,
  },
  divider: {
    height: '1px',
    background: 'linear-gradient(to right, transparent, #e2e8f0, transparent)',
    margin: `${SPACING.LG} 0`,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: FONT_SIZES.SM,
    fontWeight: '600',
    color: COLORS.PRIMARY_DARK,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  labelIcon: {
    fontSize: '14px',
    opacity: 0.7,
  },
  input: {
    padding: '10px 14px',
    fontSize: FONT_SIZES.SM,
    border: '1.5px solid #e2e8f0',
    borderRadius: BORDER_RADIUS.MEDIUM,
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    fontFamily: 'inherit',
    color: COLORS.DARK_GRAY,
    backgroundColor: '#fafbfc',
  },
  inputError: {
    borderColor: COLORS.ERROR,
    backgroundColor: '#fff5f7',
  },
  timeSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  timeSectionLabel: {
    fontSize: FONT_SIZES.SM,
    fontWeight: '600',
    color: COLORS.PRIMARY_DARK,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  row: {
    display: 'flex',
    gap: SPACING.SM,
    alignItems: 'flex-start',
  },
  timeGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
  },
  timeLabel: {
    fontSize: FONT_SIZES.XS,
    color: '#8896a6',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  timeInput: {
    fontSize: FONT_SIZES.SM,
  },
  timeDash: {
    color: '#c0c8d0',
    fontSize: '18px',
    paddingTop: '28px',
    userSelect: 'none',
  },
  errorText: {
    fontSize: FONT_SIZES.XS,
    color: COLORS.ERROR,
    fontWeight: '500',
  },
  button: {
    padding: '14px 28px',
    fontSize: FONT_SIZES.MD,
    fontWeight: '600',
    color: COLORS.WHITE,
    backgroundColor: COLORS.PRIMARY_BLUE,
    border: 'none',
    borderRadius: BORDER_RADIUS.MEDIUM,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 14px rgba(43, 151, 212, 0.35)',
    marginTop: SPACING.SM,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    letterSpacing: '0.2px',
  },
  buttonArrow: {
    fontSize: '18px',
    transition: 'transform 0.2s ease',
  },
  footerText: {
    fontSize: FONT_SIZES.XS,
    color: '#a0aab4',
    textAlign: 'center',
    marginTop: SPACING.LG,
  },
};

export default InputView;
